import clsx from 'clsx'
import {FC, ReactNode} from 'react'
import {Link, useInRouterContext} from 'react-router-dom'
import {SUPPORT_PHONE, SUPPORT_PHONE_LABEL} from '../../../../_metronic/helpers/supportContact'
import {useAuth} from '../../auth'
import {isSupportSession} from '../../auth/core/onlineRegistration'
import {useIsOnPremises} from '../../auth/core/useOnPremises'
import {hidesPayments} from '../../install-app/core/androidApp'
import {canUseTicketing} from '../../auth/core/ticketingAccess'
import {serviceRequestPath, ServiceRequestTopic} from '../../apps/ticket-management/service-request-ticket'
import {contactSentence, ServiceTariff} from '../core/serviceApi'
import './ServiceBadge.scss'

/**
 * The support line as a call link, where a request cannot be filed from the panel (see ServiceRequestLink). The number
 * is a left-to-right run of its own; the words before it stay Persian.
 */
export const SupportContactLink: FC<{label?: string; className?: string}> = ({
  label = 'تماس با پشتیبانی',
  className,
}) => (
  <span className={clsx('svc-contact', className)}>
    <i className='las la-headset' aria-hidden='true'></i>
    <span>{label}:</span>
    <a
      href={`tel:${SUPPORT_PHONE}`}
      dir='ltr'
      className='svc-contact__phone'
      aria-label={`${label} ${SUPPORT_PHONE_LABEL}`}
    >
      {SUPPORT_PHONE_LABEL}
    </a>
  </span>
)

const REQUEST_TEXT: Record<ServiceRequestTopic, string> = {
  ServiceActivation: 'ثبت درخواست فعال‌سازی',
  AccessRenewal: 'ثبت درخواست تمدید',
  LimitIncrease: 'درخواست افزایش سقف',
  Pricing: 'استعلام قیمت',
  WalletCharge: 'ثبت درخواست افزایش موجودی',
  Contract: 'ثبت درخواست پیگیری',
}

/** Whether this user may file a request from the panel: ticketing on, a business chosen, not a support session. */
export const useCanFileServiceRequest = (): boolean => {
  const {currentUser} = useAuth()
  const onPremises = useIsOnPremises()
  return (
    !onPremises &&
    canUseTicketing(currentUser) &&
    !isSupportSession(currentUser) &&
    Number(currentUser?.cpi || 0) > 0
  )
}

/**
 * «ثبت درخواست فعال‌سازی» in place of the support line (user request 2026-10-18: «توی تماس بگیرید ها بجای تلفن بشه
 * ثبت تیکت برای بخش فروش و درخواست فعال سازی»): the new-ticket page of the sales department, its subject and text
 * filled with the service's name. Where no ticket can be filed (the system administrator, a support session, an
 * on-premises install, no business yet) the support line stays.
 */
export const ServiceRequestLink: FC<{
  topic?: ServiceRequestTopic
  /** The service's key and name, written into the request. */
  feature?: string
  label?: string
  text?: string
  className?: string
}> = ({topic = 'ServiceActivation', feature, label, text, className}) => {
  const canFile = useCanFileServiceRequest()
  const inRouter = useInRouterContext()
  // No way to buy a service in the Bazaar and Myket builds of the Android app, not even through sales.
  if (hidesPayments()) return null
  if (!canFile) return <SupportContactLink className={className} />
  const path = serviceRequestPath(topic, {feature, label})
  const content = (
    <>
      <i className='las la-paper-plane' aria-hidden='true'></i>
      <span>{text || REQUEST_TEXT[topic]}</span>
    </>
  )
  // Outside the panel's router (a page rendered on its own) a plain link still reaches the ticket.
  return inRouter ? (
    <Link to={path} className={clsx('svc-request', className)}>
      {content}
    </Link>
  ) : (
    <a href={path} className={clsx('svc-request', className)}>
      {content}
    </a>
  )
}

/**
 * A «تماس بگیرید» service the business has no live access to: the server's sentence (or the same in short) and the
 * request for it, where a price and «فعال‌سازی» would otherwise be.
 */
export const ContactNote: FC<{
  tariff: Pick<ServiceTariff, 'feature' | 'label' | 'contactMessage' | 'expired' | 'expiresAtLabel'>
  /** Shown before the sentence: the free allowance that works without an access. */
  before?: ReactNode
  className?: string
}> = ({tariff, before, className}) =>
  hidesPayments() ? null : (
    <div className={clsx('svc-contact-note', className)} role='note'>
      {before && <span className='svc-contact-note__before'>{before}</span>}
      <span>{contactSentence(tariff)}</span>
      <ServiceRequestLink
        topic={tariff.expired ? 'AccessRenewal' : 'ServiceActivation'}
        feature={tariff.feature}
        label={tariff.label}
      />
    </div>
  )
